import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonToolbar } from '@ionic/angular/standalone';
import { Subscription } from 'rxjs';
import { PuzzleBoardComponent } from '../../puzzle-framework-module/puzzle-board/puzzle-board.component';
import { CrosswordPuzzleFileModel } from '../models/crossword-puzzle-file.model';
import { PuzzleGameTypesEnum } from '../../puzzle-framework-module/puzzle-game-types.enum';
import { SetPuzzleID } from '../../store/puzzle-id.actions';
import { SwedishGameState } from '../store/swedish-game.state';
import { SwedishPuzzleModel } from '../store/swedish-game.model';
import { CrosswordTileBordersModel, CrosswordTileModel } from '../models/crossword-tile.model';
import { CrosswordWordModel } from '../models/crossword-word.model';
import { CrosswordDirectionsEnum } from '../models/crossword-directions.enum';
import { CrosswordArrowClassesEnum } from '../models/crossword-arrow-classes.enum';
import { CrosswordAdDataModel } from '../models/crossword-ad-data.model';
import { Store } from '@ngxs/store';
import { PuzzleFrameworkService } from '../../puzzle-framework-module/puzzle-framework.service';
import { HelperFunctionsService } from '../../shared-module/helpers/helper-functions.service';
import {
  PuzzleSpecialKeys,
  PuzzleKeyboardService,
} from '../../shared-module/puzzle-softkeyboard/puzzle-keyboard.service';
import { AutoZoomBox, PuzzleZoomService } from '../../shared-module/puzzle-zoom/puzzle-zoom.service';
import { PuzzleZoomDirective } from 'projects/tools/src/lib/shared-module/puzzle-zoom/puzzle-zoom.directive';
import { InitSwedishGame, SaveUserInput } from '../store/swedish-game.actions';
import { Button } from '../../shared-module/buttons/button.def';
import {
  ConsumeJoker,
  FoundSaying,
  FoundSolutionWord,
  IncMoveCounter,
  SetFinished,
  SetGameProgress,
} from '../../store/puzzle-game.actions';
import { CrosswordWinScreenComponent } from '../crossword-win-screen/crossword-win-screen.component';
import {
  PUZZLE_WIN_DIALOG_BUTTONS,
  PuzzleWinDialogService,
} from '../../shared-module/puzzle-win-dialog/puzzle-win-dialog.service';
import { CrosswordHelpComponent } from '../crossword-help/crossword-help.component';
import { PuzzleHelpDialogService } from '../../shared-module/puzzle-help-dialog/puzzle-help-dialog.service';
import { DefinitionsListService } from '../crossword-sidebar/definitions-list.service';
import { PuzzleSolutionWordDialogService } from '../../shared-module/puzzle-solution-word-dialog/puzzle-solution-word-dialog.service';
import { PuzzleFrameworkEvents } from '../../puzzle-framework-module/puzzle-framework-events.enum';
import { CrosswordSidebarComponent } from '../crossword-sidebar/crossword-sidebar.component';
import { ResizeObserver } from '@juggle/resize-observer';
import { PuzzleTypeIdEnum } from '../../puzzle-framework-module/puzzle-type-id.enum';
import { CdkDrag, CdkDragDrop, CdkDragEnter, CdkDropList } from '@angular/cdk/drag-drop';
import { CrosswordTilesEnum } from '../models/crossword-tiles.enum';
import {
  LetterDeleteRestrictions,
  LetterOverwriteRestrictions,
  WordJumpOnRestrictions,
  WordJumpToRestrictions,
} from '../../store/puzzle-game-state.model';
import { PuzzleSettingsModalService } from '../../shared-module/puzzle-settings-modal/puzzle-settings-modal.service';

import { PuzzleSoftkeyboardComponent } from 'projects/tools/src/lib/shared-module/puzzle-softkeyboard/puzzle-softkeyboard.component'; // lib-puzzle-softkeyboard
import { DefinitionTileComponent } from 'projects/tools/src/lib/crossword-module/definition-tile/definition-tile.component'; // lib-app-definition-tile

export interface CrosswordBoardConfig {
  configPreset?: 'swedish' | 'crossword310' | 'gridPuzzle' | 'shakingPuzzle' | PuzzleTypeIdEnum;
  definitionBar?: 'default' | 'userSelect' | 'hide' | 'show';
  definFields?: 'default' | 'show' | 'hide' | 'block';
  crosswordSidebar?: 'default' | 'definList' | 'definAccordion' | 'wordList' | 'hide';
  fieldNumbers?: 'default' | 'show' | 'hide';
  splitWordList?: 'default' | 'no' | 'orientation' | 'length' | 'length&orientation';
  splitDefinList?: 'default' | 'no' | 'orientation';
  skipPredefined?: 'default' | 'yes' | 'no';
  constantAutoZoomDimensions?: 'default' | 'height' | 'width' | 'no';
  showWordLengths?: 'default' | 'fromPuzzleFile' | 'no';
}

@Component({
  selector: 'lib-app-crossword-board',
  templateUrl: './crossword-board.component.html',
  styleUrls: ['./crossword-board.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    IonToolbar,
    CrosswordSidebarComponent,
    PuzzleSoftkeyboardComponent,
    DefinitionTileComponent,
    PuzzleZoomDirective,
    CdkDropList,
  ],
})
export class CrosswordBoardComponent extends PuzzleBoardComponent implements OnInit, OnDestroy {
  private footerElementRef: ElementRef;
  private sidebarElementRef: CrosswordSidebarComponent;
  private maxRight = 0;
  private minLeft = 99999;
  private minTop = 99999;
  private maxBottom = 0;
  private preventSolutionWordClick = false;
  private sayingOriginalText: string;

  showSolution: boolean[] = [];
  hideFields: boolean[] = [];
  opacityAnimationTime = 0;
  private letterDeleteRestriction: LetterDeleteRestrictions;
  private letterOverwriteRestriction: LetterOverwriteRestrictions;
  private wordJumpOnRestriction: WordJumpOnRestrictions;
  private wordJumpToRestriction: WordJumpToRestrictions;
  private isCrossword221 = false;
  private highlightRightAndWrongEnabled: boolean;
  private enteredWords: string[] = [];
  public dropZoneWord: CrosswordWordModel;
  private exitedTimeout;

  @ViewChild('sidebar', { static: false }) set setSidebar(element: CrosswordSidebarComponent) {
    this.sidebarElementRef = element;
  }

  @ViewChild('footer', { static: false }) set setFooter(element: ElementRef) {
    this.footerElementRef = element;
    this.scaleBottomWord();
  }

  @Input() crosswordBoardConfig: CrosswordBoardConfig = {};

  @ViewChild('bottomWord')
  bottomWordElement: ElementRef;

  zoomBoardRef: ElementRef;
  @ViewChild('zoomBoard', { static: true }) set setZoomBoard(element: ElementRef) {
    this.zoomBoardRef = element;
    const observer = new ResizeObserver((entries) => {
      this.calculateZoomSpace();
    });
    observer.observe(this.zoomBoardRef.nativeElement);
  }

  bottomWordFocus = false;

  currentWord: CrosswordWordModel = null;
  currentTile: CrosswordTileModel = null;

  showDefinitionBar = false;

  boardWidthPx: number;
  boardHeightPx: number;

  tiles: CrosswordTileModel[];
  words: CrosswordWordModel[];

  inputTiles: CrosswordTileModel[];

  solutionTiles: CrosswordTileModel[];
  sayingTiles: CrosswordTileModel[];
  bottomWordTiles: CrosswordTileModel[] = [];
  bottomWordIsSolutionWord: boolean;

  removeSideMargins = false;

  CrosswordTilesEnum = CrosswordTilesEnum;
  solutionZoomValue: number;

  displaySweepstakeButton: boolean;
  noSpaceForSweepstakeButton: boolean;

  keyFilter = /[^a-zA-Z +]/g;

  swedishGame: SwedishPuzzleModel;

  showDefinList: boolean;

  puzzleAds: CrosswordAdDataModel[];

  bottomWordScroll: boolean;

  showWordIndices: boolean;

  finalCrosswordBoardConfig: CrosswordBoardConfig;

  availableHeight: number;

  highlightAnimateGlobal = false;

  private initialWordCheckDone = false;
  private puzzleConfig: CrosswordPuzzleFileModel;

  private columns: number;
  private rows: number;

  private numberOfInputFields: number;

  private bottomWordWidth: number;
  private bottomWordIsTouched = false;
  private bottomWordPosition: number;
  private bottomWordTouchstart: number;
  private solutionText: string;
  private sayingText: string;

  private panning: boolean;
  private adImageDragging: boolean;
  private wordsCount: number;

  private timerWindowResize: number;
  private zoomBox: AutoZoomBox;
  private solutionOriginalText: string;
  private scaleTimeout: number;
  private orderedWordIndices: number[];

  private onKeyDownSubscription: Subscription;
  private onSpecialKeyDownSubscription: Subscription;
  private definitionsListSubscription: Subscription;
  private autoZoomSubscription: Subscription;
  private winDialogSubscription: Subscription;
  private puzzleEventsSubscription: Subscription;
  private buttonPressedSubscription: Subscription;
  private helpDialogSubscription: Subscription;
  private solutionDialogSubscription: Subscription;
  private swedishGameStateSubscription: Subscription;
  private letterDeleteSubscription: Subscription;
  private letterOverwriteSubscription: Subscription;
  private highlightRindAndWrongSubscription: Subscription;

  constructor(
    public store: Store,
    public framework: PuzzleFrameworkService,
    private helperFunctions: HelperFunctionsService,
    private cd: ChangeDetectorRef,
    private el: ElementRef,
    private zoomService: PuzzleZoomService,
    private winDialogService: PuzzleWinDialogService,
    private helpDialogService: PuzzleHelpDialogService,
    private definitionsListService: DefinitionsListService,
    private solutionDialogService: PuzzleSolutionWordDialogService,
    private keyboardService: PuzzleKeyboardService,
    private settingsModalService: PuzzleSettingsModalService
  ) {
    super(store, framework);

    const sweepstakeConfig = this.framework.puzzleFrameworkConfig.solutionWordConfig.sweepstake;
    this.displaySweepstakeButton = sweepstakeConfig.enabled && sweepstakeConfig.showSolutionWordButton;

    this.removeSideMargins = this.framework.puzzleFrameworkConfig.removeSideMargins;

    this.autoZoomSubscription = this.zoomService.autoZoom.subscribe((zoomBox) => {
      this.zoomBox = zoomBox;
    });

    // When opening win dialog, inject swedish winscreencomponent and show restart and back to game button
    this.winDialogSubscription = this.winDialogService.winDialogRegistered.subscribe(async () => {
      const data = {
        solutionWord: this.solutionText,
        solutionOriginal: this.solutionOriginalText,
        sayingText: this.sayingText,
        sayingOriginal: this.sayingOriginalText,
      };

      this.winDialogService.injectComponent(CrosswordWinScreenComponent, data);
      this.winDialogService.showButtons([
        PUZZLE_WIN_DIALOG_BUTTONS.RESTART_GAME,
        PUZZLE_WIN_DIALOG_BUTTONS.BACK_TO_GAME,
      ]);
    });

    this.solutionDialogService.solutionDialogRegistered.subscribe(() => {
      this.solutionDialogService.setSolutionWords(this.solutionText, this.solutionOriginalText);
      this.solutionDialogService.setSayings(this.sayingText, this.sayingOriginalText);
    });

    // When opening swedish-help dialog, inject swedish swedish-help dialog component
    this.helpDialogSubscription = this.helpDialogService.helpDialogRegistered.subscribe(() => {
      const crosswordBoardConfig = this.finalCrosswordBoardConfig;
      this.helpDialogService.injectHelpComponent(CrosswordHelpComponent, crosswordBoardConfig);
    });

    // Subscribe to button bar buttons
    this.buttonPressedSubscription = this.framework.buttonPressed.subscribe((button: Button) => {
      switch (button) {
        case Button.Reset:
          void this.resetGame();
          break;
        case Button.Check:
          this.highlightWrong();
          break;
        case Button.Joker:
          this.solveCurrentField();
          break;
        case Button.ShowSolution:
          void this.triggerShowSolution();
          break;
      }
    });

    this.puzzleEventsSubscription = this.framework.puzzleEvents.subscribe((event) => {
      if (event === PuzzleFrameworkEvents.SOLVE_CURRENT_PUZZLE) {
        void this._winGame();
      } else if (event === PuzzleFrameworkEvents.ALMOST_SOLVE_CURRENT_PUZZLE) {
        void this._winGame(false);
      } else if (event === PuzzleFrameworkEvents.SOLVE_CURRENT_PUZZLE_BUT_ONE) {
        void this._winGame(true, 1);
      } else if (event === PuzzleFrameworkEvents.PREVIOUS_QUESTION) {
        if (this.currentWord && this.currentWord.definTile) {
          this.nextDefinitionField(true);
        } else {
          this.nextQuestion(true);
        }
      } else if (event === PuzzleFrameworkEvents.NEXT_QUESTION) {
        if (this.currentWord && this.currentWord.definTile) {
          this.nextDefinitionField();
        } else {
          this.nextQuestion();
        }
      }
    });

    // this.framework.virtualKeyboardOpen.subscribe((open) => {
    //   this.keyboardMarginBottom = open ? this.helperFunctions.getKeyboardHeight() : 0;
    //   this.detectChanges();
    // });

    this.framework.hasKeyboardInput = true;

    if (this.helperFunctions.unsupportedBrowser(true, this.framework.puzzleFrameworkConfig.nativeKeyboard)) {
      this.framework.showBrowserWarningBadge();
    }
  }

  /**
   * Solves the currently selected field, shows Snackbar with hint to the user if the field can not be solved
   *
   */
  private solveCurrentField() {
    if (!this.currentTile) {
      this.framework.showTranslatedToast('PUZZLE.snack_bar.joker_button.not_selected', 1500);
      return;
    }
    if (this.currentTile.solutionIndex) {
      this.framework.showTranslatedToast('PUZZLE.snack_bar.joker_button.is_solution_field', 1500);
      return;
    }
    if (this.currentTile.letter.toUpperCase() === this.swedishGame.userInput[this.currentTile.index].toUpperCase()) {
      this.framework.showTranslatedToast('PUZZLE.snack_bar.joker_button.already_solved', 1500);
      return;
    }
    this.onKeyPressed(this.currentTile.letter, true);
    this.store.dispatch(new ConsumeJoker({ puzzleID: this.swedishGame.puzzleID }));
  }

  /**
   * Highlights all correct solved fields
   *
   */
  private highlightCorrect() {
    const correctInputTiles = this.tiles.filter(
      (tile) =>
        tile.type === CrosswordTilesEnum.InputField &&
        tile.letter.toUpperCase() === this.swedishGame.userInput[tile.index].toUpperCase()
    );
    correctInputTiles.map((tile) => {
      tile.highlightCorrect = true;
      tile.highlightAnimate = true;
    });
    this.detectChanges();
    window.setTimeout(() => {
      correctInputTiles.map((tile) => (tile.highlightCorrect = false));
      this.detectChanges();
    }, 1000);
    window.setTimeout(() => {
      correctInputTiles.map((tile) => (tile.highlightAnimate = false));
      this.detectChanges();
    }, 1200);
  }

  /**
   * Highlights all wrong and unsolved fields
   *
   */
  private highlightWrong() {
    const wrongInputTiles = this.tiles.filter(
      (tile) =>
        tile.type === CrosswordTilesEnum.InputField &&
        tile.letter.toUpperCase() !== this.swedishGame.userInput[tile.index].toUpperCase()
    );
    wrongInputTiles.map((tile) => {
      tile.highlightWrong = true;
      tile.highlightAnimate = true;
    });
    this.detectChanges();
    window.setTimeout(() => {
      wrongInputTiles.map((tile) => (tile.highlightWrong = false));
      this.detectChanges();
    }, 1000);
    window.setTimeout(() => {
      wrongInputTiles.map((tile) => (tile.highlightAnimate = false));
      this.detectChanges();
    }, 1200);
  }

  @HostListener('window:resize')
  public onWindowResize() {
    if (this.footerElementRef) {
      this.scaleBottomWord();
    }
    this.calculateZoomSpace();
  }

  public calculateZoomSpace() {
    let availableSpace = this.el.nativeElement.offsetHeight;
    if (this.bottomWordTiles.length && this.footerElementRef) {
      availableSpace -= this.footerElementRef.nativeElement.offsetHeight;
    }

    if (this.sidebarElementRef && document.body.offsetWidth < 600) {
      availableSpace -= this.sidebarElementRef.el.nativeElement.offsetHeight;
    }

    // While debugging weird initial zoom behaviour, I found that availableHegiht is not respecting the progress bar.
    // But the problem was in move() function, and does not seem to exist now.
    // Subtracting the height of the progress bar
    // availableSpace -= 4;

    this.availableHeight = availableSpace;
  }

  /**
   * Scales solution word to the correct size
   */
  public scaleBottomWord() {
    if (!this.bottomWordElement || document.body.clientWidth === 0) {
      if (this.scaleTimeout) {
        clearTimeout(this.scaleTimeout);
        this.scaleTimeout = undefined;
      }
      this.scaleTimeout = window.setTimeout(() => {
        this.scaleBottomWord();
        this.scaleTimeout = undefined;
      }, 100);
      return;
    }

    const minZoom = this.helperFunctions.isMobileDevice()
      ? this.framework.puzzleFrameworkConfig.minZoomOutSolutionWordMobile
      : this.framework.puzzleFrameworkConfig.minZoomOutSolutionWord;

    const widthZoom = Math.max(this.bottomWordElement.nativeElement.offsetWidth / this.bottomWordWidth, minZoom);

    const solutionWordHeight = this.helperFunctions.isIOS() ? 56 : 52;

    const heightZoom = Math.max(this.bottomWordElement.nativeElement.offsetHeight / solutionWordHeight, minZoom);

    this.solutionZoomValue = Math.min(widthZoom, heightZoom, 1.5);

    const remainingSpace =
      this.bottomWordElement.nativeElement.offsetWidth - this.solutionZoomValue * this.bottomWordWidth;

    this.noSpaceForSweepstakeButton = remainingSpace < 180;

    this.bottomWordScroll = this.solutionZoomValue === minZoom && remainingSpace < 0;

    this.detectChanges();
  }

  // /**
  //  * Lifecycle method, scale solution word after the board is initialized
  //  */
  // ngAfterContentInit() {
  //   clearTimeout(this.timerWindowResize);
  //   this.timerWindowResize = window.setTimeout(() => {
  //     this.scaleSolutionWord();
  //   });
  // }

  /**
   * Restarts the game. Reinitializes puzzleGame and swedishGame
   *
   */
  private async resetGame() {
    await this.resetPuzzleGame();
    // this.initSwedishBoard();
    await this.initSwedishGame(this.swedishGame.puzzleID);
    this.zoomService.enableInitialAutoScaling();
    this.currentTile = undefined;
    this.currentWord = undefined;
    this.words.map((word: CrosswordWordModel) => {
      if (word.inputTiles.filter((tile) => tile.preset).length === word.inputTiles.length) {
        return;
      }

      word.correct = undefined;
      word.completed = false;
      word.entered = false;
    });
    // Zoom out to full board
    this.zoom(null, this.currentTile);
    this.detectChanges();
  }

  /**
   * Lifecycle method, Initializes the game when the board is added to the dom
   */
  public ngOnInit() {
    this.finalCrosswordBoardConfig = this.getFinalConfig(this.crosswordBoardConfig);

    this.zoomService.showWordLengths = this.finalCrosswordBoardConfig.showWordLengths !== 'no';

    this.framework.allowUserToggleDefinitionBar = this.finalCrosswordBoardConfig.definitionBar === 'userSelect';

    this.puzzleConfig = this.framework.puzzleConfig as CrosswordPuzzleFileModel;
    const puzzleID = this.puzzleConfig.puzzle._id;

    this.showDefinList =
      ['definList', 'definAccordion', 'wordList'].indexOf(this.finalCrosswordBoardConfig.crosswordSidebar) !== -1;

    this.store.dispatch(new SetPuzzleID({ puzzleID }));

    this.initSwedishBoard();

    this.availableHeight = this.el.nativeElement.offsetHeight;
    // While debugging weird initial zoom behaviour, I found that this offsetHeight is not respecting the definition
    // and progress bars. But the problem was in move() function, and does not seem to exist now.
    // console.log('before', this.availableHeight);
    // setTimeout(() => {
    //   this.calculateZoomSpace();
    //   console.log('after', this.availableHeight);
    // }, 100);

    this.keyboardService.keyFilterRegex = /[^a-zA-Z\s+]/g;

    this.onKeyDownSubscription = this.keyboardService.onKeyDown.subscribe((key) => {
      this.onKeyPressed(key, true);
    });

    this.onSpecialKeyDownSubscription = this.keyboardService.onSpecialKeyDown.subscribe((key) => {
      this.onSpecialKeyDown(key);
    });

    // Track puzzle type to suppress auto word-jump for blockpuzzles (221)
    this.framework.puzzleConfigLoaded$.subscribe((puzzleType) => {
      this.isCrossword221 = puzzleType === PuzzleTypeIdEnum.CROSSWORD_221;
    });

    this.definitionsListSubscription = this.definitionsListService.definitionListRegistered$.subscribe((registered) => {
      if (registered) {
        this.definitionsListService.setItems(this.words);

        this.definitionsListService.currentWord$.subscribe((word) => {
          if (word === null || word === this.currentWord) {
            return;
          }
          this.selectWordAndSetCursor(word);
        });
      }
    });

    const jokerCount = this.getJokerCount();
    this.initPuzzleGame(
      puzzleID,
      PuzzleGameTypesEnum.CrosswordPuzzle,
      jokerCount,
      this.solutionText,
      this.solutionOriginalText
    );

    this.swedishGameStateSubscription = this.store
      .select(SwedishGameState.getByID(puzzleID))
      .subscribe((swedishGame: SwedishPuzzleModel) => {
        if (!swedishGame) {
          this.initSwedishGame(puzzleID);
          return;
        }
        this.swedishGame = swedishGame;
        if (!this.initialWordCheckDone) {
          this.initialWordCheckDone = true;
          this.words.map((word) => this.checkWord(word, true));
        }
      });

    this.letterDeleteSubscription = this.store
      .select((state) => state.puzzleGameState.letterDeleteRestriction)
      .subscribe((skipLetters: LetterDeleteRestrictions) => {
        this.letterDeleteRestriction = skipLetters;
      });

    this.letterOverwriteSubscription = this.store
      .select((state) => state.puzzleGameState.letterOverwriteRestriction)
      .subscribe((skipLetters: LetterOverwriteRestrictions) => {
        this.letterOverwriteRestriction = skipLetters;
      });

    this.store
      .select((state) => state.puzzleGameState.wordJumpOnRestriction)
      .subscribe((jumpOn: WordJumpOnRestrictions) => {
        this.wordJumpOnRestriction = jumpOn;
      });

    this.store
      .select((state) => state.puzzleGameState.wordJumpToRestriction)
      .subscribe((jumpTo: WordJumpToRestrictions) => {
        this.wordJumpToRestriction = jumpTo;
      });

    this.highlightRindAndWrongSubscription = this.store
      .select((state) => state.puzzleGameState.highlightRightAndWrongEnabled)
      .subscribe((enabled) => {
        this.highlightRightAndWrongEnabled = enabled;
      });
  }

  public ngOnDestroy() {
    [
      this.onKeyDownSubscription,
      this.onSpecialKeyDownSubscription,
      this.definitionsListSubscription,
      this.autoZoomSubscription,
      this.winDialogSubscription,
      this.puzzleEventsSubscription,
      this.buttonPressedSubscription,
      this.helpDialogSubscription,
      this.solutionDialogSubscription,
      this.swedishGameStateSubscription,
      this.letterDeleteSubscription,
      this.letterOverwriteSubscription,
      this.highlightRindAndWrongSubscription,
    ].forEach((sub) => {
      if (sub) {
        try {
          sub.unsubscribe();
        } catch {
          console.warn(
            'CrosswordBoardComponent/ngOnDestroy: something went wrong unsubscribeing from a truthy subscription property'
          );
        }
      }
    });
  }

  /**
   * Returns the boards dimensions, either in height or in width, for a given number of fields
   * @param fields Number of fields
   */
  private getBoardDimension(fields: number): number {
    return fields * 50 + 2;
  }

  /**
   * Initializes a new swedishGame state
   * @param puzzleID puzzleID to init
   */
  private async initSwedishGame(puzzleID: string) {
    const userInput = this.tiles.map(() => '');

    const inputTiles = this.tiles.filter((tile) => tile.type === CrosswordTilesEnum.InputField);
    const inputFieldCount = inputTiles.length;

    inputTiles
      .filter((tile) => tile.preset === true)
      .map((tile) => {
        userInput[tile.index] = tile.letter;
      });

    // convert the userInput array to an object with the index as key
    const userInputObj: { [key: number]: string } = {};
    userInput.forEach((letter, index) => {
      userInputObj[index] = letter;
    });

    const swedishPuzzle: SwedishPuzzleModel = {
      userInput: userInputObj,
      solutionWord: this.solutionText,
      puzzleID,
      inputFieldCount,
      wordCount: this.wordsCount,
    };

    return this.store.dispatch(new InitSwedishGame(swedishPuzzle));
  }

  // solveSolutionWord => If the solution word should be filled
  // leaveOut -> how many regular fields should not be filled
  private async _winGame(solveSolutionWord = true, leaveOut = 0) {
    this.inputTiles.map(async (tile, i, all) => {
      // Skip solution fields and maybe some other fields.
      if (tile.solutionIndex || i >= all.length - leaveOut) {
        return;
      }
      await this.helperFunctions.timeout(Math.random() * 2000);
      this.onKeyPressed(tile.letter, true, tile);
    });
    await this.helperFunctions.timeout(2000);
    if (solveSolutionWord) {
      this.solutionTiles.map(async (tile, index) => {
        await this.helperFunctions.timeout(index * 100);
        this.onKeyPressed(tile.letter, true, tile);
      });
    }
  }

  /**
   * Initializes the board data structure from the puzzleFile
   */
  private initSwedishBoard() {
    this.showWordIndices = this.finalCrosswordBoardConfig.fieldNumbers === 'show';

    this.columns = parseInt(this.puzzleConfig.puzzle._colCount, 10);
    this.rows = parseInt(this.puzzleConfig.puzzle._rowCount, 10);

    this.boardHeightPx = this.getBoardDimension(parseInt(this.puzzleConfig.puzzle._rowCount, 10));
    this.boardWidthPx = this.getBoardDimension(parseInt(this.puzzleConfig.puzzle._colCount, 10));

    const hideDefinFields = this.finalCrosswordBoardConfig.definFields === 'hide';

    // Prepare puzzle tiles
    this.tiles = this.puzzleConfig.fields.field.map((tileConfig, index: number) => {
      const words = {};
      const wordList = [];
      const tile: Partial<CrosswordTileModel> = { index, words, wordList };

      switch (tileConfig._fieldType) {
        case CrosswordTilesEnum.InputField:
          tile.type = CrosswordTilesEnum.InputField;
          tile.preset = !!parseInt(tileConfig._preset, 10);
          tile.letter = tileConfig._text.toUpperCase();
          this.numberOfInputFields++;
          break;
        case CrosswordTilesEnum.DefinitionField:
          tile.type = CrosswordTilesEnum.DefinitionField;
          tile.isHidden = hideDefinFields;
          break;
        case CrosswordTilesEnum.HiddenField:
          tile.type = CrosswordTilesEnum.EmptyField;
          tile.isHidden = true;
          break;
        case CrosswordTilesEnum.DividerField:
          tile.type = CrosswordTilesEnum.EmptyField;
          tile.isDivider = true;
          break;
      }

      if (tileConfig._number) {
        tile.fieldNumber = parseInt(tileConfig._number, 10);
      }

      if (tileConfig._clue) {
        tile.solutionIndex = parseInt(tileConfig._clue, 10);
      }

      if (tileConfig._saying) {
        tile.sayingIndex = parseInt(tileConfig._saying, 10);
      }

      tile.divideRight = tileConfig._divide_right;
      tile.divideBottom = tileConfig._divide_bottom;

      tile.horizontalNumber = parseInt(tileConfig._horizontal_number, 10);
      tile.verticalNumber = parseInt(tileConfig._vertical_number, 10);

      tile.zoomBox = this.getAutoZoomBox([tile as CrosswordTileModel]);

      return tile as CrosswordTileModel;
    });

    this.inputTiles = this.tiles.filter((currentTile) => currentTile.type === CrosswordTilesEnum.InputField);

    // Prepare solution word
    this.solutionTiles = this.tiles
      .filter((tile) => tile.solutionIndex)
      .sort((a, b) => a.solutionIndex - b.solutionIndex);
    this.solutionText = this.solutionTiles.map((tile) => tile.letter).join('');

    this.prepareSolutionTiles();

    this.prepareSayingTiles();

    // Show either solution word or saying in the bottom bar
    if (this.solutionTiles.length) {
      this.bottomWordTiles = this.solutionTiles;
      this.bottomWordIsSolutionWord = true;
    } else if (this.sayingTiles.length) {
      this.bottomWordTiles = this.sayingTiles;
      this.bottomWordIsSolutionWord = false;
    }

    this.bottomWordWidth = this.getBoardDimension(this.bottomWordTiles.length);

    // Prepare words
    const wordsConfig = this.puzzleConfig.wordlist.word;
    this.wordsCount = this.puzzleConfig.wordlist.word.length;
    this.words = wordsConfig.map((wordConfig, wordIndex) => {
      const definition: string[] = [];

      if (wordConfig._defin && wordConfig._defin !== '') {
        wordConfig._defin.split('#').forEach((value, index) => {
          definition[index] = value.replace('$', '').trim();
        });
      } else if (wordConfig._definLong && wordConfig._definLong !== '') {
        definition.push(wordConfig._definLong);
      }

      const word: CrosswordWordModel = {
        index: wordIndex,
        start: {
          x: parseInt(wordConfig._startX, 10) - 1,
          y: parseInt(wordConfig._startY, 10) - 1,
        },
        word: wordConfig._text,
        definText: definition,
        definTextLong: wordConfig._definLong,
        direction: wordConfig._direction as CrosswordDirectionsEnum,
        tiles: wordConfig._puzzlefields
          .split('|')
          .map((index) => this.tiles[parseInt(index, 10)])
          .sort((a, b) => a.index - b.index),
      };

      word.zoomBox = this.getAutoZoomBox(word.tiles);

      // Remove duplicates from tiles
      word.tiles = word.tiles.filter((item, index) => word.tiles.indexOf(item) === index);

      // Store definition tiles and input tiles separately
      word.definTile = word.tiles.filter((tile) => tile.type === CrosswordTilesEnum.DefinitionField)[0];
      word.inputTiles = word.tiles.filter((tile) => tile.type === CrosswordTilesEnum.InputField);

      word.fieldNumber = word.inputTiles[0].fieldNumber;

      word.tiles.map((tile) => {
        tile.wordList.push(word);

        switch (word.direction) {
          case CrosswordDirectionsEnum.Right:
            tile.words.horizontal = word;
            break;
          case CrosswordDirectionsEnum.Down:
            tile.words.vertical = word;
            break;
        }

        if (tile.type === CrosswordTilesEnum.DefinitionField) {
          tile.defin = word.definText;
        }
      });

      word.correct = undefined;
      word.entered = false;
      word.completed = false;

      return word;
    });

    // Set fixed height or width of zoomBoxes if configured
    switch (this.finalCrosswordBoardConfig.constantAutoZoomDimensions) {
      case 'height':
        this.words.map((word) => {
          word.zoomBox.top = this.minTop;
          word.zoomBox.bottom = this.maxBottom;
        });
        break;
      case 'width':
        this.words.map((word) => {
          word.zoomBox.left = this.minLeft;
          word.zoomBox.right = this.maxRight;
        });
        break;
    }

    this.orderedWordIndices = this.words
      .map((word) => word)
      .sort((a, b) => {
        if (a.direction !== b.direction) {
          return parseInt(a.direction, 10) - parseInt(b.direction, 10);
        }
        if (a.direction === CrosswordDirectionsEnum.Right) {
          return a.inputTiles[0].index - b.inputTiles[0].index;
        } else {
          return this.rotateIndex(a.inputTiles[0].index) - this.rotateIndex(b.inputTiles[0].index);
        }
      })
      .map((word) => word.index);

    this.tiles
      .filter((tile) => tile.type === CrosswordTilesEnum.DefinitionField)
      .map((tile) => {
        tile.arrows = this.getArrowDirectionClasses(tile);
      });

    this.initAds();
  }

  private rotateIndex(oldIndex: number, cols = this.columns) {
    return Math.floor(oldIndex / cols + (oldIndex % cols) * cols);
  }

  /**
   * Listener for the onMouseDown event at the puzzle board.
   * Prevents click when dragging the puzzle field
   */
  onMouseDown(event: MouseEvent) {
    this.panning = false;
    let mouseIsDown = true;

    const startPosition = {
      x: event.clientX,
      y: event.clientY,
    };

    const onMouseMove = (e) => {
      const position = {
        x: e.clientX,
        y: e.clientY,
      };
      if (mouseIsDown) {
        const offsetX = Math.abs(position.x - startPosition.x);
        const offsetY = Math.abs(position.y - startPosition.y);
        if (Math.sqrt(Math.pow(offsetY, 2) + Math.pow(offsetX, 2)) > 5) {
          this.panning = true;
        }
      }
    };

    const onMouseUp = () => {
      mouseIsDown = false;
      setTimeout(() => {
        this.panning = false;
      });
      this.el.nativeElement.removeEventListener('mousemove', onMouseMove);
      this.el.nativeElement.removeEventListener('mouseup', onMouseUp);
    };

    this.el.nativeElement.addEventListener('mousemove', onMouseMove);
    this.el.nativeElement.addEventListener('mouseup', onMouseUp);
  }

  private selectWord(word: CrosswordWordModel, skipDefinitionListService = false) {
    this.currentWord = word;

    if (!skipDefinitionListService) {
      this.definitionsListService.setCurrentWord(word);
    }
    // Zoom to word
    this.zoom(word.zoomBox, this.currentTile);
    this.detectChanges();
  }

  private zoom(zoomBox: AutoZoomBox, currentTile: CrosswordTileModel) {
    if (this.zoomService.autoZoomEnabled && zoomBox !== this.zoomBox) {
      if (zoomBox === null) {
        this.zoomService.zoomOut();
      } else {
        this.zoomService.zoomIn(zoomBox);
      }
    } else if (this.currentTile) {
      this.zoomService.ensureCursorInZoomArea(currentTile.zoomBox);
    }
  }

  /**
   * Returns dimensions for the autoZoom
   * @param tiles Word to get dimensions for
   */
  private getAutoZoomBox(tiles: CrosswordTileModel[]): AutoZoomBox {
    let minLeft = 99999;
    let maxRight = 0;
    let minTop = 99999;
    let maxBottom = 0;

    tiles.map((tile, index) => {
      // Skip hidden definition fields
      if (
        ['hide', 'block'].indexOf(this.finalCrosswordBoardConfig.definFields) !== -1 &&
        tile.type === CrosswordTilesEnum.DefinitionField
      ) {
        return;
      }

      const row = Math.floor(tile.index / this.columns);
      const column = tile.index % this.columns;

      // Cells are 50x50, but overlap by two pixels.
      const left =
        column * 50 -
        (this.finalCrosswordBoardConfig.showWordLengths !== 'no' && tile.horizontalNumber !== undefined ? 30 : 0);
      const right = (column + 1) * 50;
      const top =
        row * 50 -
        (this.finalCrosswordBoardConfig.showWordLengths !== 'no' && tile.verticalNumber !== undefined ? 30 : 0);
      const bottom = (row + 1) * 50;

      minLeft = left < minLeft ? left : minLeft;
      maxRight = right > maxRight ? right : maxRight;
      minTop = top < minTop ? top : minTop;
      maxBottom = bottom > maxBottom ? bottom : maxBottom;
    });

    this.maxRight = Math.max(maxRight, this.maxRight);
    this.minLeft = Math.min(minLeft, this.minLeft);
    this.minTop = Math.min(minTop, this.minTop);
    this.maxBottom = Math.max(maxBottom, this.maxBottom);

    return {
      top: minTop,
      right: maxRight + 2,
      bottom: maxBottom + 2,
      left: minLeft,
    };
  }

  /**
   * Click-handler for solution word tiles
   */
  async onBottomWordTileClicked(tile: CrosswordTileModel, event?: MouseEvent) {
    if (this.preventSolutionWordClick) {
      return;
    }

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.openVirtualKeyboard();
    this.bottomWordFocus = true;
    this.currentWord = null;
    this.currentTile = tile;
    // Zoom out to full board
    this.zoom(null, this.currentTile);
  }

  private openVirtualKeyboard() {
    this.keyboardService.openSoftKeyboard();
  }

  private closeVirtualKeyboard() {
    this.keyboardService.closeSoftKeyboard();
  }

  private selectWordAndTile(word: CrosswordWordModel, tile: CrosswordTileModel) {
    if (tile.preset && this.finalCrosswordBoardConfig.skipPredefined === 'yes') {
      const noPreset = word.inputTiles.filter((t) => !t.preset);
      if (noPreset.length === 0) {
        return;
      }
      tile = noPreset[0];
    }
    this.currentTile = tile;
    this.selectWord(word);
  }

  /**
   * Click-handler for puzzle board input tiles
   */
  onInputTileClicked(tile: CrosswordTileModel, movingWithArrowKeys = false, event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // this.zoomService.stopInitialAutoScaling();

    if (this.panning) {
      return;
    }

    this.openVirtualKeyboard();

    this.bottomWordFocus = false;
    const horizontal = tile.words.horizontal;
    const vertical = tile.words.vertical;

    // if there is only one possibility
    if (!(horizontal && vertical)) {
      this.currentTile = tile;
      this.selectWordAndTile(horizontal || vertical, tile);
      return;
    }

    // if the same tile is clicked again, change direction
    if (tile === this.currentTile) {
      if (this.currentWord && this.currentWord.direction === CrosswordDirectionsEnum.Down) {
        // this.currentWord = horizontal;
        this.selectWord(horizontal);
      } else {
        // this.currentWord = vertical;
        this.selectWord(vertical);
      }
      return;
    }

    this.currentTile = tile;

    if (!movingWithArrowKeys) {
      if (vertical.correct) {
        this.selectWordAndTile(horizontal, tile);
        return;
      }

      if (horizontal.correct) {
        this.selectWordAndTile(vertical, tile);
        return;
      }

      // check if the first input of a word is clicked
      if (tile === horizontal.inputTiles[0] && tile !== vertical.inputTiles[0]) {
        // this.currentWord = horizontal;
        this.selectWordAndTile(horizontal, tile);
        return;
      }
      if (tile === vertical.inputTiles[0] && tile !== horizontal.inputTiles[0]) {
        // this.currentWord = vertical;
        this.selectWordAndTile(vertical, tile);
        return;
      }
    }

    this.selectWord(
      this.currentWord && this.currentWord.direction === CrosswordDirectionsEnum.Down ? vertical : horizontal
    );
  }

  /**
   * Initializes static puzzle-ads (the images shows in the grid).
   * pathDate can be given, but has not been used or tested yet. I guess that's interesting for the ionic app.
   */
  private initAds(pathDate?: Date) {
    const puzzleAdvertisements = this.framework.getCurrentPuzzleAds();
    if (puzzleAdvertisements) {
      // Need to map from adData to puzzleAds
      this.puzzleAds = puzzleAdvertisements.map((adData) => {
        // Get image url
        let imageUrl = adData.imageUrl;

        // If necessary, replace the date
        if (imageUrl.indexOf('$date{') !== -1) {
          // Replace $date{...} with the correct date in file string.
          if (!pathDate) {
            pathDate = this.framework.getCurrentDate();
          }
          imageUrl = this.framework.replaceDateTag(imageUrl, pathDate);
        }

        // The mapped data
        return {
          imageUrl,
          top: adData.top * 50 + 1,
          left: adData.left * 50 + 1,
          width: adData.width * 50,
          height: adData.height * 50,
          link: adData.link,
        };
      });
    }
  }

  /**
   * Returns html classes needed to display arrows in definition fields
   * @param tile Tile to get classes for
   */
  private getArrowDirectionClasses(tile: CrosswordTileModel): CrosswordArrowClassesEnum[] {
    const words = tile.wordList;

    // const keys = Object.keys(CrosswordArrowClassesEnum).filter(k => typeof CrosswordArrowClassesEnum[k as any] === 'string');
    // return keys.map(k => CrosswordArrowClassesEnum[k as any]);

    return words.map((word, index) => {
      let arrowClass: CrosswordArrowClassesEnum;

      const first = word.inputTiles[0].index;
      const second = word.inputTiles[1] ? word.inputTiles[1].index : -1;
      const definition = word.definTile.index;

      switch (first - definition) {
        case 1:
          // East
          if (second && second - first === this.columns) {
            arrowClass = CrosswordArrowClassesEnum.EastDown;
          } else if (words.length === 1) {
            arrowClass = CrosswordArrowClassesEnum.EastRightDown;
          } else if (word.direction === CrosswordDirectionsEnum.Right) {
            arrowClass = CrosswordArrowClassesEnum.EastRightTop;
          } else if (word.direction === CrosswordDirectionsEnum.Down) {
            arrowClass = CrosswordArrowClassesEnum.EastRightBottom;
          }
          break;
        case -1:
          // West
          arrowClass = CrosswordArrowClassesEnum.WestDown;
          break;
        case this.columns:
          // South
          if (second && second - first === 1) {
            arrowClass = CrosswordArrowClassesEnum.SouthRight;
          } else {
            arrowClass = CrosswordArrowClassesEnum.SouthDown;
          }
          break;
        case this.columns + 1:
          // South east
          if (second && second - first === this.columns) {
            arrowClass = CrosswordArrowClassesEnum.SouthEastDown;
          } else {
            arrowClass = CrosswordArrowClassesEnum.SouthEastRight;
          }
          break;
        case this.columns - 1:
          // South west
          arrowClass = CrosswordArrowClassesEnum.SouthWestDown;
          break;
        case -this.columns:
          // Top
          arrowClass = CrosswordArrowClassesEnum.NorthRight;
          break;
        case -this.columns + 1:
          // Top right
          arrowClass = CrosswordArrowClassesEnum.NorthEastRight;
          break;
      }
      return arrowClass;
    });
  }

  /**
   * Handler for click on static puzzle ads
   * @param link link to open
   */
  puzzleAdClicked(link: string) {
    if (this.adImageDragging || !link) {
      return;
    }
    window.open(link, '_blank');
  }

  /**
   * Handler to prevent click on puzzle images when puzzle-ads are dragged
   */
  puzzleAdMouseDown(event: MouseEvent) {
    this.adImageDragging = false;
    let mouseIsDown = true;

    const onMouseMove = () => {
      if (mouseIsDown) {
        this.adImageDragging = true;
      }
    };

    const onMouseUp = () => {
      mouseIsDown = false;
      this.el.nativeElement.removeEventListener('mousemove', onMouseMove);
      this.el.nativeElement.removeEventListener('mouseup', onMouseUp);
    };

    this.el.nativeElement.addEventListener('mousemove', onMouseMove);
    this.el.nativeElement.addEventListener('mouseup', onMouseUp);
  }

  /**
   * Mousewheel scroll on solution word
   */
  onBottomWordMouseWheel(event: WheelEvent) {
    if (!this.bottomWordScroll) {
      return;
    }
    this.bottomWordElement.nativeElement.scrollLeft = this.bottomWordElement.nativeElement.scrollLeft + event.deltaY;
  }

  onBottomWordMouseDown(event: MouseEvent) {
    if (!this.bottomWordScroll) {
      return;
    }
    this.bottomWordIsTouched = true;
    this.bottomWordTouchstart = event.pageX;
    this.bottomWordPosition = this.bottomWordElement.nativeElement.scrollLeft;
  }

  @HostListener('window:mousemove', ['$event'])
  onWindowMouseMove(event: MouseEvent) {
    if (!this.bottomWordScroll || !this.bottomWordIsTouched) {
      return;
    }
    const offset = this.bottomWordTouchstart - event.pageX;

    if (!this.preventSolutionWordClick && Math.abs(offset) > 5) {
      this.preventSolutionWordClick = true;
    }

    this.bottomWordElement.nativeElement.scroll(this.bottomWordPosition + offset, 0);
  }

  @HostListener('window:mouseup', ['$event'])
  async onWindowMouseUp(event: MouseEvent) {
    if (this.bottomWordIsTouched) {
      this.bottomWordIsTouched = false;
      event.preventDefault();
      event.stopPropagation();
    }
    await this.helperFunctions.timeout(1);
    this.preventSolutionWordClick = false;
  }

  /**
   * Touchscroll on solution word
   */
  onBottomWordTouchEnd(event: TouchEvent) {
    this.bottomWordIsTouched = false;
  }

  /**
   * Touchscroll on solution word
   */
  onBottomWordTouchMove(event: TouchEvent) {
    if (!this.bottomWordIsTouched || !this.bottomWordScroll) {
      return;
    }
    this.bottomWordElement.nativeElement.scroll(
      this.bottomWordPosition + this.bottomWordTouchstart - event.touches[0].screenX,
      0
    );
  }
  /**
   * Touchscroll on solution word
   */
  onBottomWordTouchStart(event: TouchEvent) {
    if (!this.bottomWordScroll) {
      return;
    }
    this.bottomWordIsTouched = true;
    this.bottomWordTouchstart = event.touches[0].screenX;
    this.bottomWordPosition = this.bottomWordElement.nativeElement.scrollLeft;
  }

  /**
   * React to special key down
   * Move with arrow keys
   *
   * @param key key that is pressed on the keyboard
   */
  onSpecialKeyDown(key: PuzzleSpecialKeys) {
    if (key === PuzzleSpecialKeys.Tab) {
      this.framework.puzzleEvents.emit(PuzzleFrameworkEvents.NEXT_QUESTION);
      return;
    }
    if (key === PuzzleSpecialKeys.ShiftTab) {
      this.framework.puzzleEvents.emit(PuzzleFrameworkEvents.PREVIOUS_QUESTION);
      return;
    }

    let direction: CrosswordDirectionsEnum;
    switch (key) {
      case PuzzleSpecialKeys.ArrowRight:
        direction = CrosswordDirectionsEnum.Right;
        break;
      case PuzzleSpecialKeys.ArrowDown:
        direction = CrosswordDirectionsEnum.Down;
        break;
      case PuzzleSpecialKeys.ArrowLeft:
        direction = CrosswordDirectionsEnum.Left;
        break;
      case PuzzleSpecialKeys.ArrowUp:
        direction = CrosswordDirectionsEnum.Up;
        break;
    }
    if (direction) {
      if (this.bottomWordFocus) {
        const index = this.getNextSolutionIndex(this.currentTile.index, direction, true);
        void this.onBottomWordTileClicked(this.solutionTiles[index]);
      } else {
        if (this.currentTile) {
          const index = this.getNextIndex(this.currentTile.index, direction, true);
          this.onInputTileClicked(this.tiles[index], true);
        }
      }
    }
  }

  /**
   * React to letter that is pressed. Fills or deletes input fields and jumps to next/previous field.
   * Als triggers auto-zoom-in auto-zoom-out
   * @param key Letter that is pressed
   * @param triggerChangeDetection Whether the changedetection should be triggered or not
   * @param currentTile
   */
  onKeyPressed(key: string, triggerChangeDetection = false, currentTile = this.currentTile) {
    const letter = key.toUpperCase();

    if (this.puzzleGame.finished) {
      void this.framework.showTranslatedToast('PUZZLE.snack_bar.joker_button.already_finished', 1500);
      return;
    }

    if (!currentTile) {
      void this.framework.showTranslatedToast('PUZZLE.snack_bar.joker_button.not_selected', 1500);
      return;
    }

    const puzzleID = this.swedishGame.puzzleID;
    let index = currentTile.index;

    const oldTile = currentTile;

    switch (letter) {
      case ' ': // Space
        if (!this.bottomWordFocus && currentTile.words.horizontal && currentTile.words.vertical) {
          this.selectWordAndTile(
            currentTile.words.horizontal === this.currentWord
              ? currentTile.words.vertical
              : currentTile.words.horizontal,
            currentTile
          );
        }
        break;
      case '': // Backspace
        if (!this.swedishGame.userInput[index] || currentTile.preset) {
          if (this.bottomWordFocus) {
            let prevBottomWordIndex = this.bottomWordTiles.indexOf(currentTile) - 1;
            // Skip the original solution letters as they are no input tiles.
            while (
              this.bottomWordTiles[prevBottomWordIndex] &&
              this.bottomWordTiles[prevBottomWordIndex].type === CrosswordTilesEnum.OriginalSolution
            ) {
              prevBottomWordIndex--;
            }
            if (prevBottomWordIndex >= 0) {
              currentTile = this.bottomWordTiles[prevBottomWordIndex];
              index = currentTile.index;
            }
          } else {
            let prevWordIndex = this.currentWord.inputTiles.indexOf(currentTile) - 1;

            if (prevWordIndex === -1) {
              return;
            }

            if (this.letterDeleteRestriction !== LetterDeleteRestrictions.None) {
              while (prevWordIndex >= 0) {
                let found = false;

                const otherDirectionWord =
                  this.currentWord.direction === CrosswordDirectionsEnum.Down
                    ? this.currentWord.inputTiles[prevWordIndex].words.horizontal
                    : this.currentWord.inputTiles[prevWordIndex].words.vertical;

                switch (this.letterDeleteRestriction) {
                  case LetterDeleteRestrictions.CorrectLetters:
                    found =
                      this.swedishGame.userInput[this.currentWord.inputTiles[prevWordIndex].index] !==
                      this.currentWord.inputTiles[prevWordIndex].letter;
                    break;
                  case LetterDeleteRestrictions.CorrectWords:
                    found = !otherDirectionWord || !otherDirectionWord.correct;
                    break;
                  case LetterDeleteRestrictions.CompletedWords:
                    found =
                      !otherDirectionWord ||
                      otherDirectionWord.inputTiles.filter((t) => !this.swedishGame.userInput[t.index]).length !== 0;
                    break;
                }
                if (found) {
                  break;
                }
                prevWordIndex--;
              }
            }

            if (prevWordIndex === -1) {
              void this.framework.showTranslatedToast(
                'PUZZLE.settings_modal.sections.letter_skipping.delete.toast.text',
                1500,
                {
                  handler: () => {
                    this.settingsModalService.openSettingsMenu();
                  },
                  icon: 'settings-sharp',
                  text: 'PUZZLE.settings_modal.sections.letter_skipping.delete.toast.button',
                }
              );
            }

            if (prevWordIndex >= 0) {
              currentTile = this.currentWord.inputTiles[prevWordIndex];
              index = currentTile.index;
              // Zoom to selected word
              this.zoom(this.currentWord.zoomBox, currentTile);
            } else {
              // this.zoomService.zoomOut();
              this.closeVirtualKeyboard();
              // Zoom out to full board
              this.zoom(null, currentTile);
            }
          }
        } else {
          // this.zoomService.zoomIn(this.getAutoZoomBox(this.currentWord));
          // If current word is the solution word, there is no zoomBox, so we need to prevent access.
          if (this.currentWord) {
            // Zoom to selected word
            this.zoom(this.currentWord.zoomBox, currentTile);
          }
        }

        if (this.tiles[index].preset) {
          // this.highlightWrong();
          void this.detectChangesAfterTimeout(1);
        } else {
          this.store
            .dispatch(new SaveUserInput({ puzzleID, index, letter }))
            .toPromise()
            .then(() => {
              if (triggerChangeDetection) {
                this.detectChanges();
              }
              this.onFieldFilled(oldTile, true);
              let hasUpdate = false;
              oldTile.wordList.map((word) => {
                if (this.updateEnteredWords(word.index, undefined)) {
                  hasUpdate = true;
                }
              });
              if (hasUpdate) {
                this.definitionsListService.update.emit();
              }
            });
        }

        break;
      default:
        if (this.bottomWordFocus) {
          let nextBottomWordIndex = this.bottomWordTiles.indexOf(currentTile) + 1;
          // Skip the original solution letters as they are no input tiles.
          while (
            this.bottomWordTiles[nextBottomWordIndex] &&
            this.bottomWordTiles[nextBottomWordIndex].type === CrosswordTilesEnum.OriginalSolution
          ) {
            nextBottomWordIndex++;
          }
          if (this.bottomWordTiles[nextBottomWordIndex]) {
            currentTile = this.bottomWordTiles[nextBottomWordIndex];
          }
        } else if (this.currentWord) {
          let nextWordIndex = this.currentWord.inputTiles.indexOf(currentTile) + 1;

          if (this.letterOverwriteRestriction !== LetterOverwriteRestrictions.None) {
            while (nextWordIndex < this.currentWord.inputTiles.length) {
              let found = false;

              switch (this.letterOverwriteRestriction) {
                case LetterOverwriteRestrictions.All:
                  found = !this.swedishGame.userInput[this.currentWord.inputTiles[nextWordIndex].index];
                  break;
                case LetterOverwriteRestrictions.CorrectLetters:
                  found =
                    this.swedishGame.userInput[this.currentWord.inputTiles[nextWordIndex].index] !==
                    this.currentWord.inputTiles[nextWordIndex].letter;
                  break;
                case LetterOverwriteRestrictions.CorrectWords:
                  const otherDirectionWord =
                    this.currentWord.direction === CrosswordDirectionsEnum.Down
                      ? this.currentWord.inputTiles[nextWordIndex].words.horizontal
                      : this.currentWord.inputTiles[nextWordIndex].words.vertical;
                  found = !otherDirectionWord || !otherDirectionWord.correct;
                  break;
              }

              if (found) {
                break;
              }

              nextWordIndex++;
            }
          }

          if (this.currentWord.inputTiles[nextWordIndex]) {
            currentTile = this.currentWord.inputTiles[nextWordIndex];
            // Zoom to selected word
            this.zoom(this.currentWord.zoomBox, currentTile);
          } else if (this.currentWord.correct) {
            this.closeVirtualKeyboard();
            // Zoom out depending on the settings minAutoZoomOut[Mobile]
            this.zoom(
              {
                top: -1,
                right: -1,
                bottom: -1,
                left: -1,
              },
              currentTile
            );
          }
        }

        if (this.tiles[index].preset) {
          // this.highlightWrong();
          void this.detectChangesAfterTimeout(1);
        } else {
          this.store
            .dispatch(new SaveUserInput({ puzzleID, index, letter }))
            .toPromise()
            .then(() => {
              if (triggerChangeDetection) {
                this.detectChanges();
              }
              this.onFieldFilled(oldTile);
            });
        }

        break;
    }

    if (this.finalCrosswordBoardConfig.skipPredefined === 'yes' && currentTile.preset) {
      return;
    }

    this.currentTile = currentTile;
  }

  /**
   * Checks if solution word is found or puzzle is found
   * Also checks fields words and highlights them
   * @param tile tile that has been filled
   * @param deleted
   */
  private onFieldFilled(tile: CrosswordTileModel, deleted = false) {
    // update progress
    const correct = this.inputTiles.reduce((previousValue, currentTile) => {
      if (currentTile.letter.toUpperCase() === this.swedishGame.userInput[currentTile.index].toUpperCase()) {
        return previousValue + 1;
      }
      return previousValue;
    }, 0);
    const progress = correct / this.inputTiles.length;
    this.store.dispatch(new SetGameProgress({ puzzleID: this.swedishGame.puzzleID, progress }));

    this.store.dispatch(new IncMoveCounter({ puzzleID: this.swedishGame.puzzleID }));

    if (correct === this.inputTiles.length) {
      this.store.dispatch(new SetFinished({ puzzleID: this.swedishGame.puzzleID }));
    }

    // Check words
    if (deleted) {
      tile.wordList.map((word) => {
        word.correct = undefined;
        word.completed = false;
      });
      this.definitionsListService.update.emit();
    } else {
      tile.wordList.map((word) => {
        this.checkWord(word);
      });

      // If tile is part of solution word, check solution word
      if (
        !this.puzzleGame.solutionFound &&
        tile.solutionIndex &&
        this.solutionTiles
          .filter((currentTile) => currentTile.type !== CrosswordTilesEnum.OriginalSolution) // Only check real input values
          .reduce(
            (previousValue, currentTile) =>
              currentTile.letter === this.swedishGame.userInput[currentTile.index] && previousValue,
            true
          )
      ) {
        this.store.dispatch(new FoundSolutionWord({ puzzleID: this.swedishGame.puzzleID }));
      }

      // if tile is part of saying, check saying
      if (
        !this.puzzleGame.sayingFound &&
        tile.sayingIndex &&
        this.sayingTiles
          .filter((t) => t.type !== CrosswordTilesEnum.OriginalSolution)
          .reduce(
            (previousValue, currentTile) =>
              currentTile.letter === this.swedishGame.userInput[currentTile.index] && previousValue,
            true
          )
      ) {
        this.store.dispatch(new FoundSaying({ puzzleID: this.swedishGame.puzzleID }));
      }
    }
  }

  /**
   * Returns the next grid index for the given index
   * @param index Origin index
   * @param direction Direction to move to
   * @param movingWithArrowKeys Whether the user is moving with the arrow keys
   *
   */
  private getNextIndex(index: number, direction: CrosswordDirectionsEnum, movingWithArrowKeys = false): number {
    let newTile = null;
    const oldIndex = index;
    while (!newTile) {
      switch (direction) {
        case CrosswordDirectionsEnum.Down:
          index += this.columns;
          if (index >= this.tiles.length) {
            index = movingWithArrowKeys ? index - this.tiles.length : oldIndex;
          }
          break;
        case CrosswordDirectionsEnum.Right:
          index += 1;
          if (index % this.columns === 0) {
            index = movingWithArrowKeys ? index - this.columns : oldIndex;
          }
          break;
        case CrosswordDirectionsEnum.Up:
          index -= this.columns;
          if (index < 0) {
            index = movingWithArrowKeys ? index + this.tiles.length : oldIndex;
          }
          break;
        case CrosswordDirectionsEnum.Left:
          index -= 1;
          if (index < 0 || index % this.columns === this.columns - 1) {
            index = movingWithArrowKeys ? index + this.columns : oldIndex;
          }
          break;
      }
      if (this.tiles[index].type === CrosswordTilesEnum.InputField) {
        newTile = this.tiles[index];
      }
    }
    return index;
  }

  private async detectChangesAfterTimeout(timeout = 0) {
    await this.helperFunctions.timeout(timeout);
    this.detectChanges();
  }

  /**
   * Detect changes as long the board is not destroyed
   */
  private detectChanges() {
    if (!this.cd[`destroyed`]) {
      this.cd.detectChanges();
    }
  }

  /**
   * Check if the word is correct or not.
   * @param wordToCheck Word to check
   * @param skipHighlight Whether the highlighting should be skipped
   */
  private checkWord(wordToCheck: CrosswordWordModel = this.currentWord, skipHighlight: boolean = false) {
    let complete = true;

    // This calculates complete and correct at the same time
    const correct = wordToCheck.inputTiles.reduce((previousValue, tile) => {
      complete = complete && !!this.swedishGame.userInput[tile.index];
      return this.swedishGame && tile.letter === this.swedishGame.userInput[tile.index] && previousValue;
    }, true);

    wordToCheck.completed = complete;

    if (!complete) {
      if (this.updateEnteredWords(wordToCheck.index, undefined)) {
        this.definitionsListService.update.emit();
      }
      return;
    }

    const enteredWord = wordToCheck.inputTiles.map((tile) => this.swedishGame.userInput[tile.index]).join('');
    this.updateEnteredWords(wordToCheck.index, enteredWord);

    // If highlighting is disabled and we are checking the current word, zoom out
    // Must check for false, as undefined is a not good, but on initialising the puzzle it might be undefined
    if (this.highlightRightAndWrongEnabled === false) {
      // Not a crossing word
      if (this.currentWord === wordToCheck) {
        // Pretend to be correct
        wordToCheck.correct = correct;

        // If we are not jumping, zoom out
        if (
          this.wordJumpOnRestriction === WordJumpOnRestrictions.None ||
          (this.wordJumpOnRestriction === WordJumpOnRestrictions.CorrectWord && !correct) ||
          // This puzzle type does not support auto-jump
          this.isCrossword221
        ) {
          this.closeVirtualKeyboard();
          // Zoom out some pixels around the box
          // const zoom_out_a_bit = {
          //   top: this.currentWord.zoomBox.top - 150,
          //   right: this.currentWord.zoomBox.right + 150,
          //   bottom: this.currentWord.zoomBox.bottom + 150,
          //   left: this.currentWord.zoomBox.left - 150,
          // };
          // this.zoom(zoom_out_a_bit, this.currentTile);
          // Zoom out depending on the settings minAutoZoomOut[Mobile]
          this.zoom(
            {
              top: -1,
              right: -1,
              bottom: -1,
              left: -1,
            },
            this.currentTile
          );
        } else {
          // If we are jumping on correct or complete
          this.framework.puzzleEvents.emit(PuzzleFrameworkEvents.NEXT_QUESTION);
        }
      }
      this.definitionsListService.update.emit();
      return;
    }

    // Must check for change, otherwise already correct words will be highlighted again.
    // But if the current word is already correct we can do it, otherwise the word might not be highlighted at all.
    if (wordToCheck.correct !== correct || this.currentWord === wordToCheck) {
      wordToCheck.correct = correct;
      this.definitionsListService.update.emit();
      this.cd.detectChanges();
      this.definitionsListService.update.emit();

      if (skipHighlight) {
        return;
      }
      if (wordToCheck.correct) {
        // Not a crossing word
        if (this.currentWord === wordToCheck) {
          // If the we are not jumping, zoom out (puzzletype 221 never jumps)
          if (this.wordJumpOnRestriction === WordJumpOnRestrictions.None || this.isCrossword221) {
            // this.zoomService.zoomOut();
            this.closeVirtualKeyboard();
            // Zoom out depending on the settings minAutoZoomOut[Mobile]
            this.zoom(
              {
                top: -1,
                right: -1,
                bottom: -1,
                left: -1,
              },
              this.currentTile
            );
          } else {
            // We are jumping on correct or complete
            window.setTimeout(() => {
              this.framework.puzzleEvents.emit(PuzzleFrameworkEvents.NEXT_QUESTION);
            }, 1000);
          }
        }

        wordToCheck.tiles.map((tile) => {
          tile.highlightAnimate = true;
          tile.highlightCorrect = true;
        });
        this.detectChanges();
        window.setTimeout(() => {
          wordToCheck.tiles.map((tile) => (tile.highlightCorrect = false));
          this.detectChanges();
        }, 1000);
        window.setTimeout(() => {
          wordToCheck.tiles.map((tile) => (tile.highlightAnimate = false));
          this.detectChanges();
        }, 1200);
      } else {
        // Word seems is filled but not correct

        // If the current word is checked, and we are jumping on complete
        if (this.currentWord === wordToCheck && this.wordJumpOnRestriction === WordJumpOnRestrictions.CompleteWord) {
          window.setTimeout(() => {
            this.framework.puzzleEvents.emit(PuzzleFrameworkEvents.NEXT_QUESTION);
          }, 1000);
        }

        wordToCheck.tiles.map((tile) => {
          tile.highlightAnimate = true;
          tile.highlightWrong = true;
        });
        this.detectChanges();
        window.setTimeout(() => {
          wordToCheck.tiles.map((tile) => (tile.highlightWrong = false));
          this.detectChanges();
        }, 1000);
        window.setTimeout(() => {
          wordToCheck.tiles.map((tile) => (tile.highlightAnimate = false));
          this.detectChanges();
        }, 1200);
      }
    }
  }

  /**
   * Returns the next index in the solution word
   * @param index Current index
   * @param direction Directin to move
   * @param movingWithArrowKeys Whether the user is moving with the arrow keys
   */
  private getNextSolutionIndex(index: number, direction: CrosswordDirectionsEnum, movingWithArrowKeys = false) {
    let currentIndex = this.solutionTiles.indexOf(this.tiles[index]);
    switch (direction) {
      case CrosswordDirectionsEnum.Right:
        currentIndex += 1;
        // Skip the original solution letters as they are no input tiles.
        while (
          this.solutionTiles[currentIndex] &&
          this.solutionTiles[currentIndex].type === CrosswordTilesEnum.OriginalSolution
        ) {
          currentIndex++;
        }
        break;
      case CrosswordDirectionsEnum.Left:
        currentIndex -= 1;
        // Skip the original solution letters as they are no input tiles.
        while (
          this.solutionTiles[currentIndex] &&
          this.solutionTiles[currentIndex].type === CrosswordTilesEnum.OriginalSolution
        ) {
          currentIndex--;
        }
        break;
    }

    if (currentIndex < 0) {
      currentIndex = this.solutionTiles.length - 1;
      // Skip the original solution letters as they are no input tiles.
      while (
        this.solutionTiles[currentIndex] &&
        this.solutionTiles[currentIndex].type === CrosswordTilesEnum.OriginalSolution
      ) {
        currentIndex--;
      }
    } else if (currentIndex >= this.solutionTiles.length) {
      currentIndex = 0;
      // Skip the original solution letters as they are no input tiles.
      while (
        this.solutionTiles[currentIndex] &&
        this.solutionTiles[currentIndex].type === CrosswordTilesEnum.OriginalSolution
      ) {
        currentIndex++;
      }
    }
    return currentIndex;
  }

  /**
   * Callback invoked when the user clicks on a definition field
   * @param word Word that is selected
   */
  onDefinitionTileClicked(word: CrosswordWordModel) {
    // this.zoomService.stopInitialAutoScaling();
    this.bottomWordFocus = false;
    this.openVirtualKeyboard();
    // this.selectWord(word);
    // this.currentTile = this.currentWord.inputTiles[0];
    this.selectWordAndSetCursor(word, true);
  }

  // TODO this is duplicated in definition-bar.component.ts, should be in some service
  private getFinalConfig(crosswordBoardConfig: CrosswordBoardConfig) {
    let {
      crosswordSidebar,
      definFields,
      splitDefinList,
      splitWordList,
      fieldNumbers,
      definitionBar,
      skipPredefined,
      constantAutoZoomDimensions,
      showWordLengths,
    } = crosswordBoardConfig;

    const { configPreset } = crosswordBoardConfig;

    let presetValues: CrosswordBoardConfig = {};

    switch (configPreset) {
      case PuzzleTypeIdEnum.CROSSWORD_770:
      case PuzzleTypeIdEnum.CROSSWORD_771:
        presetValues = {
          definitionBar: 'hide',
          crosswordSidebar: 'hide',
          definFields: 'hide',
          splitDefinList: 'no',
          splitWordList: 'no',
          fieldNumbers: 'hide',
          skipPredefined: 'yes',
          constantAutoZoomDimensions: 'width',
          showWordLengths: 'no',
        };
        break;
      case PuzzleTypeIdEnum.CROSSWORD_221:
        presetValues = {
          definitionBar: 'hide',
          crosswordSidebar: 'wordList',
          definFields: 'block',
          splitDefinList: 'no',
          splitWordList: 'length&orientation',
          fieldNumbers: 'hide',
          skipPredefined: 'no',
          constantAutoZoomDimensions: 'no',
          showWordLengths: 'no',
        };
        break;
      case PuzzleTypeIdEnum.CROSSWORD_20:
        presetValues = {
          definitionBar: 'hide',
          crosswordSidebar: 'hide',
          definFields: 'show',
          splitDefinList: 'no',
          splitWordList: 'no',
          fieldNumbers: 'hide',
          skipPredefined: 'no',
          constantAutoZoomDimensions: 'no',
          showWordLengths: 'no',
        };
        break;
      case PuzzleTypeIdEnum.CROSSWORD_310:
        presetValues = {
          definitionBar: 'hide',
          crosswordSidebar: 'definList',
          definFields: 'hide',
          splitDefinList: 'orientation',
          splitWordList: 'no',
          fieldNumbers: 'show',
          skipPredefined: 'no',
          constantAutoZoomDimensions: 'no',
          showWordLengths: 'no',
        };
        break;
      case PuzzleTypeIdEnum.CROSSWORD_265:
        presetValues = {
          definitionBar: 'hide',
          crosswordSidebar: 'definList',
          definFields: 'hide',
          splitDefinList: 'no',
          splitWordList: 'no',
          fieldNumbers: 'hide',
          skipPredefined: 'no',
          constantAutoZoomDimensions: 'no',
          showWordLengths: 'no',
        };
        break;
      case PuzzleTypeIdEnum.CROSSWORD_252:
        presetValues = {
          definitionBar: 'hide',
          crosswordSidebar: 'wordList',
          definFields: 'hide',
          splitDefinList: 'no',
          splitWordList: 'length',
          fieldNumbers: 'hide',
          skipPredefined: 'no',
          constantAutoZoomDimensions: 'no',
          showWordLengths: 'fromPuzzleFile',
        };
        break;
      default:
        // Swedish
        presetValues = {
          definitionBar: 'userSelect',
          crosswordSidebar: 'hide',
          definFields: 'show',
          splitDefinList: 'no',
          splitWordList: 'no',
          fieldNumbers: 'hide',
          skipPredefined: 'no',
          constantAutoZoomDimensions: 'no',
          showWordLengths: 'no',
        };
        break;
    }

    crosswordSidebar =
      crosswordSidebar && crosswordSidebar !== 'default' ? crosswordSidebar : presetValues.crosswordSidebar;
    definFields = definFields && definFields !== 'default' ? definFields : presetValues.definFields;
    splitDefinList = splitDefinList && splitDefinList !== 'default' ? splitDefinList : presetValues.splitDefinList;
    splitWordList = splitWordList && splitWordList !== 'default' ? splitWordList : presetValues.splitWordList;
    fieldNumbers = fieldNumbers && fieldNumbers !== 'default' ? fieldNumbers : presetValues.fieldNumbers;
    definitionBar = definitionBar && definitionBar !== 'default' ? definitionBar : presetValues.definitionBar;
    skipPredefined = skipPredefined && skipPredefined !== 'default' ? skipPredefined : presetValues.skipPredefined;
    constantAutoZoomDimensions =
      constantAutoZoomDimensions && constantAutoZoomDimensions !== 'default'
        ? constantAutoZoomDimensions
        : presetValues.constantAutoZoomDimensions;
    showWordLengths = showWordLengths && showWordLengths !== 'default' ? showWordLengths : presetValues.showWordLengths;

    return {
      configPreset,
      crosswordSidebar,
      definFields,
      splitDefinList,
      splitWordList,
      fieldNumbers,
      definitionBar,
      skipPredefined,
      constantAutoZoomDimensions,
      showWordLengths,
    };
  }

  nextDefinitionField(previous = false) {
    // Where are we now?
    const currentIndex = this.currentWord ? this.currentWord.definTile.index : -1;
    // How often have we tried?
    let trial = 0;
    // We are looking for this word
    let word: CrosswordWordModel | null = null;

    // Search for a word if we don't have one yet but stop if we went through the whole list to prevent infinite looping
    while (!word && trial < this.tiles.length) {
      // Calculate the next index, depending on trial number
      // Modulo it, so it wraps around
      let nextIndex = (currentIndex + 1 + trial) % this.tiles.length;
      if (previous) {
        // Make it positive and modulo it, so it wraps around
        nextIndex = (currentIndex - 1 - trial + this.tiles.length) % this.tiles.length;
      }

      // The next tile must be a definition tile
      if (this.tiles[nextIndex].type !== CrosswordTilesEnum.DefinitionField) {
        trial++;
        continue;
      }

      // Select the word from the tile
      word = this.tiles[nextIndex].words.horizontal || this.tiles[nextIndex].words.vertical;

      // Check if we should skip this.
      // We definitely skip if if word is undefined. Even though I currently don't unterstand why it would be undefined.
      // This happened in block puzzles.
      if (
        word == null ||
        (this.wordJumpToRestriction === WordJumpToRestrictions.IncompleteWord && word.completed) ||
        (this.wordJumpToRestriction === WordJumpToRestrictions.IncorrectWord && word.correct)
      ) {
        word = null;
        trial++;
      }
    }

    // If we found a word, select it
    if (word) {
      this.selectWordAndSetCursor(word, true);
      this.definitionsListService.setCurrentWord(word);
    }
  }

  nextQuestion(previous = false) {
    // Where are we now?
    const currentIndex = this.currentWord ? this.orderedWordIndices.indexOf(this.currentWord.index) : -1;
    // How often have we tried?
    let trial = 0;
    // We are looking for this word
    let word: CrosswordWordModel | null = null;

    // Search for a word if we don't have one yet but stop if we went through the whole list twice to prevent infinite looping
    while (!word && trial < this.orderedWordIndices.length) {
      // Calculate the next index, depending on trial number
      // Modulo it, so it wraps around
      let nextIndex = (currentIndex + 1 + trial) % this.orderedWordIndices.length;
      if (previous) {
        // Make it positive and modulo it, so it wraps around
        nextIndex = (currentIndex - 1 - trial + this.orderedWordIndices.length) % this.orderedWordIndices.length;
      }
      // Select the next word
      word = this.words[this.orderedWordIndices[nextIndex]];

      // Check if we should skip this
      if (
        (this.wordJumpToRestriction === WordJumpToRestrictions.IncompleteWord && word.completed) ||
        (this.wordJumpToRestriction === WordJumpToRestrictions.IncorrectWord && word.correct)
      ) {
        word = null;
        trial++;
      }
    }

    // If we found a word, select it
    if (word) {
      this.selectWordAndSetCursor(word, true);
      this.definitionsListService.setCurrentWord(word);
    }
  }

  selectWordAndSetCursor(word: CrosswordWordModel, forceSelectFirst = false) {
    this.openVirtualKeyboard();
    this.bottomWordFocus = false;
    if (word !== this.currentWord) {
      // This skipped definition list service, but I don't know why.
      // Without, the Definition Bar is not updated, so I activate it now.
      // this.selectWord(word, true);
      this.selectWord(word);
    }
    if (forceSelectFirst || word.inputTiles.indexOf(this.currentTile) === -1) {
      this.currentTile = word.inputTiles[0];
    }
    this.detectChanges();
  }

  preventDefault(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Returns word to drop to
   *
   * @private
   */
  private getDropZoneWord(dropZoneTile: CrosswordTileModel, droppedWord: CrosswordWordModel): CrosswordWordModel {
    // check if one or more words are possible
    const possibleWords = dropZoneTile.wordList.filter(
      (word) =>
        word.inputTiles.length === droppedWord.inputTiles.length &&
        !word.inputTiles.reduce((previous: boolean, tile) => previous && !!tile.preset, true)
    );

    let dropZoneWord: CrosswordWordModel;

    switch (possibleWords.length) {
      case 0:
        return undefined;
        break;
      case 1:
        // if only one possibility, use it
        dropZoneWord = possibleWords[0];
        break;
      default:
        if (!!dropZoneTile.horizontalNumber !== !!dropZoneTile.verticalNumber) {
          // if field has only one horizontal or vertical number
          dropZoneWord = !!dropZoneTile.horizontalNumber // drop only on regarding horizontal or vertical word
            ? dropZoneTile.words.horizontal
            : dropZoneTile.words.vertical;
        } else {
          // if there are two possible words, use current direction or use the dragged word direction
          const currentDirection =
            this.finalCrosswordBoardConfig.splitWordList.indexOf('orientation') === -1
              ? this.currentWord
                ? this.currentWord.direction
                : CrosswordDirectionsEnum.Left
              : droppedWord.direction;
          dropZoneWord =
            currentDirection === CrosswordDirectionsEnum.Down
              ? dropZoneTile.words.vertical
              : dropZoneTile.words.horizontal;
        }
        break;
    }
    return dropZoneWord;
  }

  async onDropListDropped(event: CdkDragDrop<CrosswordWordModel>, dropZoneTile: CrosswordTileModel) {
    this.dropZoneWord = undefined;
    this.highlightAnimateGlobal = false;

    if (!event.isPointerOverContainer) {
      return;
    }

    const droppedWord = event.item.data as CrosswordWordModel;

    const dropZoneWord = this.getDropZoneWord(dropZoneTile, droppedWord);

    if (
      this.words.indexOf(droppedWord) === -1 ||
      !dropZoneWord ||
      (this.finalCrosswordBoardConfig.splitWordList.indexOf('orientation') !== -1 &&
        droppedWord.direction !== dropZoneWord.direction)
    ) {
      return;
    }

    this.currentWord = dropZoneWord;

    // const dropZoneWordIndices = dropZoneWord.inputTiles.map(w => w.index);
    const animationStartIndex = dropZoneWord.inputTiles.indexOf(dropZoneTile);
    let distance = 1;
    const maxDistance = Math.max(animationStartIndex, dropZoneWord.inputTiles.length - 1 - animationStartIndex);
    const correct = droppedWord === dropZoneWord;

    dropZoneWord.correct = correct;
    droppedWord.entered = true;
    dropZoneWord.completed = true;

    let current = animationStartIndex;

    /**
     * Fills and highlights a field
     */
    const fillField = (
      isCorrect: boolean = correct,
      tile: CrosswordTileModel = dropZoneWord.inputTiles[current],
      letter: string = droppedWord.inputTiles[current].letter
    ) => {
      if (!tile.preset) {
        this.store
          .dispatch(new SaveUserInput({ puzzleID: this.puzzleGame.puzzleID, index: tile.index, letter }))
          .toPromise()
          .then(() => {
            this.onFieldFilled(tile);
          });
      }

      if (!this.highlightRightAndWrongEnabled) {
        return;
      }
      tile.highlightAnimate = true;
      if (isCorrect) {
        tile.highlightCorrect = true;
      } else {
        tile.highlightWrong = true;
      }
    };

    fillField();

    this.currentTile = undefined;
    this.detectChanges();

    while (distance <= maxDistance) {
      await this.helperFunctions.timeout(50);
      current = animationStartIndex - distance;
      if (current >= 0) {
        fillField();
      }

      current = animationStartIndex + distance;
      if (current < dropZoneWord.inputTiles.length) {
        fillField();
      }

      this.currentTile = undefined;

      this.detectChanges();

      distance += 1;
    }

    // this.currentWord = undefined;
    // this.currentTile = undefined;

    this.currentTile = dropZoneWord.inputTiles[dropZoneWord.inputTiles.length - 1];

    await this.helperFunctions.timeout(1000);
    dropZoneWord.inputTiles.map((tile) => (tile.highlightCorrect = false));
    dropZoneWord.inputTiles.map((tile) => (tile.highlightWrong = false));
    this.detectChanges();

    await this.helperFunctions.timeout(200);
    dropZoneWord.inputTiles.map((tile) => (tile.highlightAnimate = false));
    this.detectChanges();

    if (!this.highlightRightAndWrongEnabled || correct) {
      // Zoom out depending on the settings minAutoZoomOut[Mobile]
      this.zoom(
        {
          top: -1,
          right: -1,
          bottom: -1,
          left: -1,
        },
        this.currentTile
      );
    }
  }

  private getJokerCount() {
    const jokerConfig = this.framework.puzzleFrameworkConfig.jokerButton;
    switch (jokerConfig.unit) {
      case 'absolute':
        return Math.ceil(jokerConfig.value);
      case 'percent':
        return Math.ceil((this.inputTiles.length * jokerConfig.value) / 100);
      default:
        return 0;
    }
  }

  private checkDirectionAndMakeWordSelectable(wordTiles: CrosswordTileModel[], wordText: string) {
    const indices = wordTiles.map((tile) => tile.index); // .sort((a, b) => a - b);

    let previous;
    let horizontal = true;
    let vertical = true;

    for (const index of indices) {
      if (!horizontal && !vertical) {
        break;
      }
      if (previous === undefined) {
        previous = index;
        continue;
      }

      horizontal = horizontal && index - previous === 1 && !this.tiles[index].words.horizontal;
      vertical = vertical && index - previous === this.columns && !this.tiles[index].words.vertical;
      previous = index;
    }

    if (horizontal || vertical) {
      const word: CrosswordWordModel = {
        index: -1,
        start: {
          x: wordTiles[0].index % this.columns,
          y: Math.floor(wordTiles[0].index / this.columns),
        },
        word: wordText,
        definText: [''],
        definTextLong: '',
        direction: horizontal ? CrosswordDirectionsEnum.Right : CrosswordDirectionsEnum.Down,
        tiles: wordTiles,
        inputTiles: wordTiles,
      };

      word.zoomBox = this.getAutoZoomBox(word.tiles);

      wordTiles.map((tile) => {
        tile.wordList.push(word);

        if (horizontal) {
          tile.words.horizontal = word;
        } else {
          tile.words.vertical = word;
        }
      });
    }
  }

  private prepareSayingTiles() {
    // Prepare saying
    this.sayingTiles = this.tiles.filter((t) => t.sayingIndex).sort((a, b) => a.sayingIndex - b.sayingIndex);
    this.sayingText = this.sayingTiles.map((t) => t.letter).join('');

    if (this.sayingTiles.length > 0) {
      this.checkDirectionAndMakeWordSelectable(this.sayingTiles, this.sayingText);

      const directions = [
        {
          name: 'top',
          value: -this.columns,
        },
        {
          name: 'right',
          value: 1,
        },
        {
          name: 'bottom',
          value: this.columns,
        },
        {
          name: 'left',
          value: -1,
        },
      ];

      // check saying tiles for connected groups and draw border around them
      this.sayingTiles.map((tile) => {
        const borders: CrosswordTileBordersModel = {
          bottom: true,
          left: true,
          right: true,
          top: true,
        };

        for (const direction of directions) {
          const directionTile = this.tiles[tile.index + direction.value];
          if (directionTile && directionTile.sayingIndex) {
            borders[direction.name] = false;
          }
        }

        tile.borders = borders;
      });
    }

    if (this.puzzleConfig.saying && this.sayingText.length < this.puzzleConfig.saying._original.length) {
      const sayingOriginal = this.puzzleConfig.saying._original;
      this.sayingOriginalText = sayingOriginal;

      this.sayingTiles = this.addOriginalTilesToBottomWord(sayingOriginal, this.sayingTiles);
    }
  }

  private prepareSolutionTiles() {
    // Prepare solution word
    this.solutionTiles = this.tiles
      .filter((tile) => tile.solutionIndex)
      .sort((a, b) => a.solutionIndex - b.solutionIndex);

    this.solutionText = this.solutionTiles.map((tile) => tile.letter).join('');

    // Make solutionWord selectable if configured, but only if a solution word exists
    if (this.solutionTiles.length > 0) {
      // check if solution word is connected horizontally or vertically
      this.checkDirectionAndMakeWordSelectable(this.solutionTiles, this.solutionText);
    }

    // // We might need to add chars from the original solution to display it.
    if (this.puzzleConfig.solution && this.solutionText.length < this.puzzleConfig.solution._original.length) {
      const solutionOriginal = this.puzzleConfig.solution._original;
      this.solutionOriginalText = solutionOriginal;

      // Use the new solution tiles.
      this.solutionTiles = this.addOriginalTilesToBottomWord(solutionOriginal, this.solutionTiles);
    }
  }

  private addOriginalTilesToBottomWord(bottomOriginal, bottomTiles) {
    let wordIndex = 0;
    const newSolutionTiles: CrosswordTileModel[] = [];

    // Check each letter in the original solution word.
    // for (let i = 0; i < original.length; i++) {
    for (const originalLetter of bottomOriginal) {
      if (bottomTiles[wordIndex] && bottomTiles[wordIndex].letter.toUpperCase() === originalLetter.toUpperCase()) {
        // If the original letter exists as tile, use the tile
        newSolutionTiles.push(bottomTiles[wordIndex]);
        wordIndex++;
      } else {
        // otherwise create a new "tile" with the original letter.
        newSolutionTiles.push({
          index: 0,
          words: {},
          wordList: [],
          arrows: [],
          type: CrosswordTilesEnum.OriginalSolution,
          letter: originalLetter,
          isHidden: false,
        });
      }
    }

    return newSolutionTiles;
  }

  private async triggerShowSolution() {
    const valueFadeInOutTime = 1000;
    const solutionFadeInOutTime = 200;
    const revealSolutionTime = this.framework.puzzleFrameworkConfig.showSolutionTime;

    this.zoomService.autoZoom.emit(null);

    const showSolutionFields = this.tiles.map(
      (t) => t.type === CrosswordTilesEnum.InputField && t.letter !== this.swedishGame.userInput[t.index]
    );

    // const hasValues = Object.values(this.swedishGame.userInput).filter(i => i.length).length !== 0

    // Fade out values
    this.opacityAnimationTime = valueFadeInOutTime;
    this.hideFields = showSolutionFields;
    this.detectChanges();
    await this.helperFunctions.timeout(valueFadeInOutTime);

    // Fade in solution
    this.opacityAnimationTime = solutionFadeInOutTime;
    this.showSolution = showSolutionFields;
    this.hideFields = [];
    this.detectChanges();
    await this.helperFunctions.timeout(solutionFadeInOutTime);

    // Hold solution
    await this.helperFunctions.timeout(revealSolutionTime);

    // Fade out solution
    this.hideFields = showSolutionFields;
    this.detectChanges();
    await this.helperFunctions.timeout(solutionFadeInOutTime);

    // Fade in values
    this.opacityAnimationTime = valueFadeInOutTime;
    this.detectChanges();

    this.showSolution = [];
    this.hideFields = [];
    this.detectChanges();
    await this.helperFunctions.timeout(valueFadeInOutTime);

    this.opacityAnimationTime = 0;
  }

  private updateEnteredWords(index: number, enteredWord: string): boolean {
    let hasUpdate = false;

    if (this.enteredWords[index] !== enteredWord) {
      this.enteredWords[index] = enteredWord;
      this.words.map((word) => {
        const entered = this.enteredWords.indexOf(word.word) !== -1;
        if (word.entered !== entered) {
          word.entered = entered;
          hasUpdate = true;
        }
      });
    }

    return hasUpdate;
  }

  cdkDropListEntered(event: CdkDragEnter<CrosswordWordModel>, tile: CrosswordTileModel) {
    if (this.exitedTimeout) {
      clearTimeout(this.exitedTimeout);
      this.exitedTimeout = undefined;
    }
    const droppedWord = event.item.data as CrosswordWordModel;
    this.dropZoneWord = this.getDropZoneWord(tile, droppedWord);

    // if (!this.dropZoneWord) {
    //   this.cdkDropListExited();
    //   return;
    // }

    this.highlightAnimateGlobal = true;
    this.detectChanges();
  }

  cdkDropListExited(isBoard = false) {
    this.dropZoneWord = undefined;
    if (this.exitedTimeout) {
      clearTimeout(this.exitedTimeout);
    }
    this.exitedTimeout = setTimeout(() => {
      this.highlightAnimateGlobal = false;
      this.detectChanges();
      this.exitedTimeout = undefined;
    }, 200);
  }

  sortPredicate(index: number, item: CdkDrag<number>) {
    return true;
  }
}
